"use client";

import Link from "@/components/atsa/Link";
import { useEffect, useMemo, useState } from "react";
import { AppShell } from "@/components/atsa/AppShell";
import { ATSA_PROFILE_2026 } from "@/lib/atsa/atsaConfig";
import { formatTime } from "@/lib/atsa/utils";

const sections = [
  ["Numbers and Differences", "/atsa/memory-differences"],
  ["Value Assignments", "/atsa/value-assignments"],
  ["Spatial Relationship", "/atsa/spatial-awareness"],
  ["Radar Simulation", "/atsa/radar-simulation"],
  ["Personality Assessment", "/atsa/personality-assessment"],
  ["Reading Comprehension", "/atsa/reading-comprehension"],
  ["Logical Reasoning", "/atsa/logical-reasoning"],
] as const;

type StoredSession = {
  startedAt: number;
  breakStartedAt: number | null;
  breakUsedSeconds: number;
  completed: boolean[];
};

const STORAGE_KEY = "atsa-full-mock-v2";

export default function FullMockPage() {
  const [session, setSession] = useState<StoredSession | null>(null);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored) setSession(JSON.parse(stored) as StoredSession);
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    if (session) window.localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  }, [session]);

  const currentBreakSeconds = session?.breakStartedAt ? Math.floor((now - session.breakStartedAt) / 1000) : 0;
  const totalBreakUsed = (session?.breakUsedSeconds ?? 0) + currentBreakSeconds;
  const elapsedWall = session ? Math.floor((now - session.startedAt) / 1000) : 0;
  const activeElapsed = Math.max(0, elapsedWall - totalBreakUsed);
  const activeRemaining = Math.max(0, ATSA_PROFILE_2026.totalActiveSeconds - activeElapsed);
  const breakRemaining = Math.max(0, ATSA_PROFILE_2026.totalBreakSeconds - totalBreakUsed);
  const nextIndex = session?.completed.findIndex((value) => !value) ?? 0;
  const completeCount = session?.completed.filter(Boolean).length ?? 0;
  const allComplete = completeCount === sections.length;

  function start() {
    setSession({ startedAt: Date.now(), breakStartedAt: null, breakUsedSeconds: 0, completed: sections.map(() => false) });
  }

  function toggleBreak() {
    setSession((current) => {
      if (!current) return current;
      if (current.breakStartedAt) {
        const used = Math.floor((Date.now() - current.breakStartedAt) / 1000);
        return { ...current, breakStartedAt: null, breakUsedSeconds: current.breakUsedSeconds + used };
      }
      if (breakRemaining <= 0) return current;
      return { ...current, breakStartedAt: Date.now() };
    });
  }

  function markComplete(index: number) {
    setSession((current) => current ? { ...current, completed: current.completed.map((value, itemIndex) => itemIndex === index ? true : value) } : current);
  }

  function reset() {
    window.localStorage.removeItem(STORAGE_KEY);
    setSession(null);
  }

  const status = useMemo(() => session?.breakStartedAt ? "BREAK ACTIVE" : allComplete ? "COMPLETE" : "ACTIVE", [allComplete, session?.breakStartedAt]);

  return (
    <AppShell>
      <section className="panel full-mock-panel">
        <div className="panel-header">
          <p className="eyebrow">FULL-SITTING PROTOCOL</p>
          <h2>ATSA-format full mock</h2>
          <p>This controller preserves the official 2:49 active-time ceiling and 30-minute break bank. Each module still opens separately so browser failures do not destroy the entire sitting.</p>
        </div>
        {!session ? (
          <>
            <ol className="instructions">
              <li>Use Fidelity Simulation inside every section.</li>
              <li>Return here after completing a section and mark it complete.</li>
              <li>Take breaks only between sections. Pause the active clock with the break control below.</li>
              <li>This controller does not claim the exact operational section order for the final three sections.</li>
            </ol>
            <button className="primary-button" onClick={start}>Start full mock</button>
          </>
        ) : (
          <>
            <div className="mock-clock-grid">
              <div><span>Status</span><strong>{status}</strong></div>
              <div><span>Active time remaining</span><strong>{formatTime(activeRemaining)}</strong></div>
              <div><span>Break time remaining</span><strong>{formatTime(breakRemaining)}</strong></div>
              <div><span>Sections complete</span><strong>{completeCount}/7</strong></div>
            </div>
            <div className="mock-actions">
              {!allComplete && <button className="secondary-button" onClick={toggleBreak}>{session.breakStartedAt ? "End break" : "Start break"}</button>}
              <button className="text-button danger" onClick={reset}>Reset sitting</button>
            </div>
            <div className="mock-section-list">
              {sections.map(([title, href], index) => (
                <article className={`mock-section-row ${session.completed[index] ? "complete" : index === nextIndex ? "current" : ""}`} key={href}>
                  <span>{index + 1}</span>
                  <div><strong>{title}</strong><small>{session.completed[index] ? "Completed" : index === nextIndex ? "Next section" : "Pending"}</small></div>
                  <div className="mock-row-actions">
                    <Link className="secondary-button link-button" href={href}>Open</Link>
                    {!session.completed[index] && <button className="primary-button" onClick={() => markComplete(index)}>Mark complete</button>}
                  </div>
                </article>
              ))}
            </div>
          </>
        )}
      </section>
    </AppShell>
  );
}
